import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Search, User, MapPin, Euro, Calendar, Clock,
  Phone, Mail, Eye, UserPlus, CheckCircle, Building2, Home
} from 'lucide-react';
import fileTextIcon from '../../assets/img3d/contrat.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactRequestsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: (searchParams.get('status') || '').toUpperCase(),
    priority: '',
    search: '',
    assignedToAdmin: searchParams.get('filter') === 'unassigned' ? 'unassigned' : ''
  });

  useEffect(() => {
    fetchRequests();
  }, [filters]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);
      if (filters.assignedToAdmin) params.append('assignedToAdmin', filters.assignedToAdmin);

      const response = await axios.get(`${API}/admin/contact-requests?${params.toString()}`);
      const requestsData = response.data;
      const items = Array.isArray(requestsData) ? requestsData : (requestsData?.items || []);
      
      // Normalize data: convert camelCase to snake_case and handle nested objects
      const normalizedRequests = items.map(request => ({
        ...request,
        // Handle camelCase fields from API
        seeker_name: request.seekerName || request.seeker_name,
        seeker_email: request.seekerEmail || request.seeker_email,
        seeker_phone: request.seekerPhone || request.seeker_phone,
        seeker_message: request.seekerMessage || request.seeker_message,
        property_id: request.propertyId || request.property_id,
        budget_min: request.budgetMin ? Number(request.budgetMin) : (request.budget_min ? Number(request.budget_min) : null),
        budget_max: request.budgetMax ? Number(request.budgetMax) : (request.budget_max ? Number(request.budget_max) : null),
        financing_status: request.financingStatus || request.financing_status,
        move_in_date: request.moveInDate || request.move_in_date,
        current_situation: request.currentSituation || request.current_situation,
        has_property_to_sell: request.hasPropertyToSell !== undefined ? request.hasPropertyToSell : request.has_property_to_sell,
        assigned_to_admin_id: request.assignedToAdminId || request.assigned_to_admin_id,
        assigned_to_professional_id: request.assignedToProfessionalId || request.assigned_to_professional_id,
        created_at: request.createdAt || request.created_at,
        // Handle nested objects
        property: request.property || null,
        assigned_to_admin: request.assignedToAdmin || request.assigned_to_admin || null,
        assigned_to_professional: request.assignedToProfessional || request.assigned_to_professional || null,
      }));
      
      setRequests(normalizedRequests);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // TODO: Réactiver quand la logique de score sera implémentée
  // const getScoreBadge = (score) => {
  //   if (score >= 75) {
  //     return <Badge className="bg-red-100 text-red-800">🔥 {score}</Badge>;
  //   } else if (score >= 50) {
  //     return <Badge className="bg-yellow-100 text-yellow-800">⚡ {score}</Badge>;
  //   } else {
  //     return <Badge className="bg-gray-100 text-gray-800">❄️ {score}</Badge>;
  //   }
  // };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-orange-100 text-orange-800', label: 'Nouveau' },
      qualified: { color: 'bg-purple-100 text-purple-800', label: 'Qualifié' },
      assigned: { color: 'bg-orange-100 text-orange-800', label: 'Assigné' },
      visit_planned: { color: 'bg-yellow-100 text-yellow-800', label: 'Visite' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Converti' },
      lost: { color: 'bg-red-100 text-red-800', label: 'Perdu' }
    };

    const normalized = (status || 'new').toString().toLowerCase();
    const config = statusConfig[normalized] || statusConfig.new;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non disponible';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date invalide';
      
      const now = new Date();
      const diffMinutes = Math.floor((now - date) / 60000);

      if (diffMinutes < 1) {
        return 'À l\'instant';
      } else if (diffMinutes < 60) {
        return `Il y a ${diffMinutes}min`;
      } else if (diffMinutes < 1440) {
        return `Il y a ${Math.floor(diffMinutes / 60)}h`;
      } else {
        return date.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
    } catch (error) {
      return 'Date invalide';
    }
  };

  const formatMoveInDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return null;
    }
  };

  const getAssignedAdminName = (assignedToAdmin) => {
    if (!assignedToAdmin) return null;
    if (typeof assignedToAdmin === 'string') return assignedToAdmin;
    const firstName = assignedToAdmin.firstName || '';
    const lastName = assignedToAdmin.lastName || '';
    return `${firstName} ${lastName}`.trim() || assignedToAdmin.email || 'Admin';
  };

  const getAssignedProfessionalName = (assignedToProfessional) => {
    if (!assignedToProfessional) return null;
    if (typeof assignedToProfessional === 'string') return assignedToProfessional;
    const firstName = assignedToProfessional.firstName || '';
    const lastName = assignedToProfessional.lastName || '';
    return `${firstName} ${lastName}`.trim() || assignedToProfessional.email || 'Professionnel';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleAssignToMe = async (requestId) => {
    try {
      await axios.post(`${API}/admin/contact-requests/${requestId}/assign-to-me`);
      fetchRequests();
    } catch (error) {
      console.error('Error assigning request:', error);
    }
  };

  return (
    <Layout
      headerConfig={{
        icon: fileTextIcon,
        iconColor: 'bg-white',
        title: 'Demandes de Contact',
        subtitle: loading ? 'Chargement...' : `${requests.length} demande(s) trouvée(s)`,
        primaryAction: 'Tableau de Bord',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/admin'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recherche
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Nom, email, téléphone..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="NEW">Nouveau</SelectItem>
                  <SelectItem value="QUALIFIED">Qualifié</SelectItem>
                  <SelectItem value="ASSIGNED">Assigné</SelectItem>
                  <SelectItem value="VISIT_PLANNED">Visite Planifiée</SelectItem>
                  <SelectItem value="COMPLETED">Converti</SelectItem>
                  <SelectItem value="LOST">Perdu</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* TODO: Réactiver quand la logique de score sera implémentée */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priorité
              </label>
              <Select
                value={filters.priority || 'all'}
                onValueChange={(value) => setFilters({ ...filters, priority: value === 'all' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="high">🔥 Haute</SelectItem>
                  <SelectItem value="medium">⚡ Moyenne</SelectItem>
                  <SelectItem value="low">❄️ Basse</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            <div>
        
              <Select
                value={filters.assignedToAdmin || 'all'}
                onValueChange={(value) => setFilters({ ...filters, assignedToAdmin: value === 'all' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="unassigned">Non assignées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 flex space-x-2">
            <Button
              onClick={() => setFilters({ status: '', priority: '', search: '', assignedToAdmin: '' })}
              variant="outline"
            >
              Réinitialiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF9F68]"></div>
        </div>
      ) : requests.length > 0 ? (
        <div className="space-y-4">
          {requests.map((request) => {
            const assignedAdminName = getAssignedAdminName(request.assigned_to_admin);
            const assignedProfessionalName = getAssignedProfessionalName(request.assigned_to_professional);
            const moveInDateFormatted = formatMoveInDate(request.move_in_date);
            const property = request.property || {};
            const visitDate =
              request.visitDate && !isNaN(new Date(request.visitDate).getTime())
                ? new Date(request.visitDate)
                : null;
            
            return (
              <Card
                key={request.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/contact-requests/${request.id}`)}
              >
                <CardContent className="p-6">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    {/* TODO: Réactiver quand la logique de score sera implémentée */}
                    {/* Score */}
                    {/* <div className="col-span-1 flex justify-center">
                      {getScoreBadge(request.score || 0)}
                    </div> */}

                    {/* Chercheur Info */}
                    <div className="col-span-3">
                      <div className="font-semibold text-gray-900 text-base">{request.seeker_name || 'Non renseigné'}</div>
                      <div className="text-sm text-gray-600 flex items-center mt-1">
                        <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{request.seeker_email || 'N/A'}</span>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center mt-1">
                        <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                        {request.seeker_phone || 'N/A'}
                      </div>
                      {request.has_property_to_sell && (
                        <div className="text-xs text-orange-600 mt-1 flex items-center">
                          <Home className="h-3 w-3 mr-1" />
                          A une propriété à vendre
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-3">
                        {getStatusBadge(request.status || 'NEW')}
                        <div className="text-xs text-gray-500">
                          {formatDate(request.created_at)}
                        </div>
                      </div>
                    </div>

                    {/* Property Info */}
                    <div className="col-span-2">
                      <div className="text-sm text-gray-600 flex items-center mb-1">
                        Propriété
                      </div>
                      {property.title ? (
                        <>
                          <div className="font-semibold text-gray-900 text-sm truncate" title={property.title}>
                            {property.title}
                          </div>
                          {property.location && (
                            <div className="text-xs text-gray-500 flex items-center mt-1">
                              <span className="truncate">{property.location}</span>
                            </div>
                          )}
                          {property.price && (
                            <div className="text-xs text-gray-700 mt-1 flex items-center">
                              {formatPrice(Number(property.price))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-xs text-gray-400">Non disponible</div>
                      )}
                    </div>

                    {/* Budget */}
                    <div className="col-span-2">
                      <div className="text-sm text-gray-600">Budget</div>
                      <div className="font-semibold text-gray-900 text-sm">
                        {request.budget_min && request.budget_max ? (
                          <>{formatPrice(request.budget_min)} - {formatPrice(request.budget_max)}</>
                        ) : request.budget_min ? (
                          <>À partir de {formatPrice(request.budget_min)}</>
                        ) : request.budget_max ? (
                          <>Jusqu'à {formatPrice(request.budget_max)}</>
                        ) : (
                          'Non renseigné'
                        )}
                      </div>
                      {moveInDateFormatted && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          Emménagement: {moveInDateFormatted}
                        </div>
                      )}
                    </div>

                    {/* Visit Info */}
                    <div className="col-span-2">
                      <div className="text-sm text-gray-600 mb-1">Visite</div>
                      {request.visitScheduled && visitDate ? (
                        <div
                          className="text-xs text-orange-600 mt-1 cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/admin/visits', { state: { selectedRequestId: request.id } });
                          }}
                        >
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>
                              {visitDate.toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                          <div className="flex items-center mt-1">
                            <Clock className="h-3 w-3 mr-1" />
                            <span>
                              {visitDate.toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 mt-1">
                          Aucune visite planifiée
                        </div>
                      )}
                    </div>

                    {/* Assignment & Actions */}
                    <div className="col-span-3">
                      <div className="text-sm text-gray-600 mb-1">Assignation</div>
                      {assignedAdminName ? (
                        <Badge variant="secondary" className="text-xs">
                          <User className="h-3 w-3 mr-1" />
                          {assignedAdminName}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Non assigné</Badge>
                      )}
                      {assignedProfessionalName && (
                        <div className="mt-1">
                          <Badge variant="outline" className="text-xs">
                            <UserPlus className="h-3 w-3 mr-1" />
                            {assignedProfessionalName}
                          </Badge>
                        </div>
                      )}

                      <div className="mt-3 flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/contact-requests/${request.id}`);
                          }}
                          aria-label="Voir la demande"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!request.assigned_to_admin_id && (
                          <Button
                            size="sm"
                            className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssignToMe(request.id);
                            }}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Moi
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-20 text-center">
            <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucune demande trouvée
            </h3>
            <p className="text-gray-600">
              Essayez de modifier vos filtres de recherche
            </p>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
};

export default ContactRequestsPage;
